/**
 * Camera math for overlaying world-space progress STLs on the review 360 viewer.
 *
 * Matches progress-tracking-inference Blender placement:
 *   world_xy = affine(plan_x, plan_y)
 *   z = cad_height
 *   base_yaw = -(image_angle + rotation)
 *
 * App ViewerPosition yaw is floor-plan oriented (Pannellum receives yaw − image_angle).
 * Combined look direction equals convertViewerPositionToTarget with yaw = appYaw + rotation.
 */

import {
  applyAffine,
  FloorProgressTransform,
} from './progressFloorTransform'

export type WorldVec3 = { x: number; y: number; z: number }

export type ProgressPanoCameraPose = {
  position: WorldVec3
  /** Blender Z euler degrees: -(imageAngle + rotation). */
  baseYawDeg: number
}

/**
 * Same formula as progress-tracking-inference `compute_camera_config`.
 */
export function computeProgressPanoCameraPose(args: {
  planX: number
  planY: number
  affine: FloorProgressTransform
  cadHeight: number
  imageAngle: number
  rotation?: number
}): ProgressPanoCameraPose {
  const rotation = args.rotation ?? 0
  const [wx, wy] = applyAffine(args.affine, [args.planX, args.planY])
  return {
    position: { x: wx, y: wy, z: args.cadHeight },
    baseYawDeg: -1 * (args.imageAngle + rotation),
  }
}

/**
 * Unit look direction in world space (Z-up) for the app ViewerPosition.
 * `rotation` is progress_translation calibration (degrees).
 */
export function progressPanoLookDirection(
  appYawDeg: number,
  appPitchDeg: number,
  rotation = 0,
): WorldVec3 {
  const yaw = ((appYawDeg + rotation) * Math.PI) / 180
  const pitch = (appPitchDeg * Math.PI) / 180
  const cosPitch = Math.cos(pitch)
  return {
    x: cosPitch * Math.sin(yaw),
    y: cosPitch * Math.cos(yaw),
    z: Math.sin(pitch),
  }
}

export function progressPanoLookTarget(
  position: WorldVec3,
  appYawDeg: number,
  appPitchDeg: number,
  rotation = 0,
  distance = 50000,
): WorldVec3 {
  const dir = progressPanoLookDirection(appYawDeg, appPitchDeg, rotation)
  return {
    x: position.x + distance * dir.x,
    y: position.y + distance * dir.y,
    z: position.z + distance * dir.z,
  }
}

/**
 * Convert Pannellum horizontal FOV (degrees) to Three.js vertical FOV (degrees).
 */
export function hfovToVerticalFovDeg(hfovDeg: number, aspect: number): number {
  const safeAspect = aspect > 1e-6 ? aspect : 1
  const hfovRad = (hfovDeg * Math.PI) / 180
  const vfovRad = 2 * Math.atan(Math.tan(hfovRad / 2) / safeAspect)
  return (vfovRad * 180) / Math.PI
}

/**
 * Identity used by the overlay: image_angle cancels between Blender base yaw and
 * Pannellum's angleOffset, leaving world yaw = appYaw + rotation.
 */
export function effectiveWorldYawDeg(
  appYawDeg: number,
  imageAngle: number,
  rotation: number,
): number {
  const pannellumYaw = appYawDeg - imageAngle
  const baseYaw = -1 * (imageAngle + rotation)
  return baseYaw - pannellumYaw
}
